import React from 'react';
// import { useNavigate } from 'react-router-dom';
// import { useAuthenticator } from '@aws-amplify/ui-react';
import Layout from '../components/Layout';
import '../styles/homepage.css';

const howItWorksSteps = [
  {
    step: "1",
    title: "Describe Your Vision",
    description: "Simply tell us what you want to build. 'I need a microservices e-commerce platform with real-time inventory management.'",
    icon: "💬"
  },
  {
    step: "2", 
    title: "Intelligent Questioning",
    description: "Our AI conducts a structured interview, asking about business context, technical requirements, scalability needs, and integration points. Refine answers iteratively until requirements are complete.",
    icon: "🧠"
  },
  {
    step: "3",
    title: "Complete Architecture Package",
    description: "Receive TOGAF-compliant artifacts, ArchiMate-compatible diagrams, enterprise documents (SAD, HLD, SDD), and cloud-optimized infrastructure code.",
    icon: "📋"
  }
];

const sampleArtifacts = [
  {
    title: "TOGAF OV-1 View",
    description: "High-level operational concept",
    thumbnail: "data:image/svg+xml;base64," + btoa(`
      <svg width="200" height="150" xmlns="http://www.w3.org/2000/svg">
        <rect width="200" height="150" fill="#f8f9fa" stroke="#dee2e6"/>
        <rect x="20" y="20" width="60" height="40" fill="#1e3c72" rx="5"/>
        <text x="50" y="42" text-anchor="middle" fill="white" font-size="10">Business</text>
        <rect x="100" y="20" width="60" height="40" fill="#2a5298" rx="5"/>
        <text x="130" y="42" text-anchor="middle" fill="white" font-size="10">System</text>
        <rect x="60" y="80" width="80" height="40" fill="#FF9900" rx="5"/>
        <text x="100" y="102" text-anchor="middle" fill="white" font-size="10">Operations</text>
        <line x1="80" y1="60" x2="100" y2="80" stroke="#333" stroke-width="2"/>
        <line x1="130" y1="60" x2="120" y2="80" stroke="#333" stroke-width="2"/>
        <text x="100" y="140" text-anchor="middle" fill="#666" font-size="12">TOGAF OV-1</text>
      </svg>
    `),
    type: "Enterprise Architecture"
  },
  {
    title: "UML Sequence Diagram",
    description: "API interaction flows",
    thumbnail: "data:image/svg+xml;base64," + btoa(`
      <svg width="200" height="150" xmlns="http://www.w3.org/2000/svg">
        <rect width="200" height="150" fill="#f8f9fa" stroke="#dee2e6"/>
        <line x1="40" y1="20" x2="40" y2="130" stroke="#1e3c72" stroke-width="2" stroke-dasharray="5,5"/>
        <line x1="100" y1="20" x2="100" y2="130" stroke="#1e3c72" stroke-width="2" stroke-dasharray="5,5"/>
        <line x1="160" y1="20" x2="160" y2="130" stroke="#1e3c72" stroke-width="2" stroke-dasharray="5,5"/>
        <rect x="20" y="10" width="40" height="20" fill="#2a5298" rx="3"/>
        <text x="40" y="22" text-anchor="middle" fill="white" font-size="8">Client</text>
        <rect x="80" y="10" width="40" height="20" fill="#2a5298" rx="3"/>
        <text x="100" y="22" text-anchor="middle" fill="white" font-size="8">API</text>
        <rect x="140" y="10" width="40" height="20" fill="#2a5298" rx="3"/>
        <text x="160" y="22" text-anchor="middle" fill="white" font-size="8">DB</text>
        <line x1="40" y1="50" x2="100" y2="50" stroke="#FF9900" stroke-width="2" marker-end="url(#arrowhead)"/>
        <text x="70" y="45" text-anchor="middle" fill="#333" font-size="8">request</text>
        <line x1="100" y1="70" x2="160" y2="70" stroke="#FF9900" stroke-width="2" marker-end="url(#arrowhead)"/>
        <text x="130" y="65" text-anchor="middle" fill="#333" font-size="8">query</text>
        <line x1="160" y1="90" x2="100" y2="90" stroke="#28a745" stroke-width="2" marker-end="url(#arrowhead)"/>
        <text x="130" y="85" text-anchor="middle" fill="#333" font-size="8">result</text>
        <line x1="100" y1="110" x2="40" y2="110" stroke="#28a745" stroke-width="2" marker-end="url(#arrowhead)"/>
        <text x="70" y="105" text-anchor="middle" fill="#333" font-size="8">response</text>
        <defs><marker id="arrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto"><polygon points="0 0, 10 3.5, 0 7" fill="#333"/></marker></defs>
      </svg>
    `),
    type: "Technical Diagram"
  },
  {
    title: "Solution Architecture Document",
    description: "Complete SAD template",
    thumbnail: "data:image/svg+xml;base64," + btoa(`
      <svg width="200" height="150" xmlns="http://www.w3.org/2000/svg">
        <rect width="200" height="150" fill="#ffffff" stroke="#dee2e6"/>
        <rect x="10" y="10" width="180" height="20" fill="#1e3c72"/>
        <text x="100" y="22" text-anchor="middle" fill="white" font-size="10" font-weight="bold">Solution Architecture Document</text>
        <line x1="15" y1="40" x2="185" y2="40" stroke="#333" stroke-width="1"/>
        <text x="15" y="50" fill="#333" font-size="8">1. Executive Summary</text>
        <text x="15" y="62" fill="#333" font-size="8">2. Business Requirements</text>
        <text x="15" y="74" fill="#333" font-size="8">3. Technical Architecture</text>
        <text x="15" y="86" fill="#333" font-size="8">4. System Components</text>
        <text x="15" y="98" fill="#333" font-size="8">5. Integration Points</text>
        <text x="15" y="110" fill="#333" font-size="8">6. Security Framework</text>
        <text x="15" y="122" fill="#333" font-size="8">7. Deployment Strategy</text>
        <rect x="150" y="45" width="35" height="25" fill="#FF9900" rx="3"/>
        <text x="167" y="58" text-anchor="middle" fill="white" font-size="8">Diagram</text>
        <rect x="150" y="75" width="35" height="25" fill="#28a745" rx="3"/>
        <text x="167" y="88" text-anchor="middle" fill="white" font-size="8">Matrix</text>
        <rect x="150" y="105" width="35" height="25" fill="#6f42c1" rx="3"/>
        <text x="167" y="118" text-anchor="middle" fill="white" font-size="8">Flow</text>
      </svg>
    `),
    type: "Enterprise Document"
  },
  {
    title: "AWS Infrastructure Diagram",
    description: "Cloud deployment architecture",
    thumbnail: "data:image/svg+xml;base64," + btoa(`
      <svg width="200" height="150" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <marker id="arrowhead" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto">
            <polygon points="0 0, 8 3, 0 6" fill="#FF9900"/>
          </marker>
          <!-- AWS Cloud Icon -->
          <g id="aws-cloud">
            <path d="M2,8 Q2,4 6,4 Q10,2 14,4 Q18,4 18,8 Q16,10 14,10 L6,10 Q2,10 2,8" fill="#FF9900" stroke="#E47911" stroke-width="0.5"/>
            <circle cx="5" cy="7" r="1" fill="white"/>
            <circle cx="9" cy="6" r="1" fill="white"/>
            <circle cx="13" cy="7" r="1" fill="white"/>
          </g>
          <!-- ALB Icon -->
          <g id="alb-icon">
            <rect width="16" height="12" fill="#FF9900" rx="2"/>
            <rect x="2" y="2" width="12" height="2" fill="white"/>
            <rect x="2" y="5" width="8" height="2" fill="white"/>
            <rect x="2" y="8" width="10" height="2" fill="white"/>
          </g>
          <!-- ECS Icon -->
          <g id="ecs-icon">
            <rect width="16" height="12" fill="#FF9900" rx="2"/>
            <rect x="2" y="2" width="4" height="3" fill="white" rx="1"/>
            <rect x="7" y="2" width="4" height="3" fill="white" rx="1"/>
            <rect x="2" y="6" width="4" height="3" fill="white" rx="1"/>
            <rect x="7" y="6" width="4" height="3" fill="white" rx="1"/>
            <circle cx="13" cy="4" r="1" fill="white"/>
            <circle cx="13" cy="8" r="1" fill="white"/>
          </g>
          <!-- RDS Icon -->
          <g id="rds-icon">
            <rect width="16" height="12" fill="#3F48CC" rx="2"/>
            <ellipse cx="8" cy="4" rx="5" ry="2" fill="white"/>
            <rect x="3" y="4" width="10" height="4" fill="white"/>
            <ellipse cx="8" cy="8" rx="5" ry="2" fill="white"/>
            <ellipse cx="8" cy="6" rx="3" ry="1" fill="#3F48CC"/>
          </g>
          <!-- Lambda Icon -->
          <g id="lambda-icon">
            <rect width="16" height="12" fill="#FF9900" rx="2"/>
            <path d="M3,9 L6,3 L8,3 L11,9 L9,9 L8.5,7.5 L5.5,7.5 L5,9 Z M6.2,6 L7.8,6 L7,4.2 Z" fill="white"/>
          </g>
          <!-- ElastiCache Icon -->
          <g id="redis-icon">
            <rect width="16" height="12" fill="#C925D1" rx="2"/>
            <circle cx="5" cy="6" r="2.5" fill="white"/>
            <circle cx="11" cy="6" r="2.5" fill="white"/>
            <circle cx="8" cy="6" r="1" fill="#C925D1"/>
          </g>
          <!-- S3 Icon -->
          <g id="s3-icon">
            <rect width="16" height="12" fill="#569A31" rx="2"/>
            <path d="M3,3 L13,3 L13,9 L3,9 Z" fill="white"/>
            <path d="M4,4 L12,4 L11,8 L5,8 Z" fill="#569A31"/>
            <circle cx="6" cy="6" r="0.5" fill="white"/>
            <circle cx="8" cy="6" r="0.5" fill="white"/>
            <circle cx="10" cy="6" r="0.5" fill="white"/>
          </g>
        </defs>
        
        <rect width="200" height="150" fill="#f8f9fa" stroke="#dee2e6"/>
        
        <!-- AWS Cloud Background -->
        <rect x="5" y="5" width="190" height="140" fill="#FFF7E6" stroke="#FF9900" stroke-width="2" stroke-dasharray="8,4" rx="8"/>
        <use href="#aws-cloud" x="10" y="8" transform="scale(0.8)"/>
        <text x="30" y="18" fill="#FF9900" font-size="9" font-weight="bold">AWS Cloud</text>
        
        <!-- VPC Container -->
        <rect x="15" y="25" width="170" height="110" fill="#E6F3FF" stroke="#0073BB" stroke-width="1" stroke-dasharray="4,2" rx="5"/>
        <text x="20" y="37" fill="#0073BB" font-size="8" font-weight="bold">VPC (10.0.0.0/16)</text>
        
        <!-- Public Subnet -->
        <rect x="25" y="45" width="70" height="35" fill="#E8F5E8" stroke="#4CAF50" stroke-width="1" rx="3"/>
        <text x="28" y="55" fill="#4CAF50" font-size="7">Public Subnet</text>
        
        <!-- Private Subnet -->
        <rect x="105" y="45" width="70" height="80" fill="#FFF0F0" stroke="#F44336" stroke-width="1" rx="3"/>
        <text x="108" y="55" fill="#F44336" font-size="7">Private Subnet</text>
        
        <!-- ALB -->
        <use href="#alb-icon" x="35" y="62" transform="scale(0.8)"/>
        <text x="43" y="82" text-anchor="middle" fill="#333" font-size="6">ALB</text>
        
        <!-- ECS -->
        <use href="#ecs-icon" x="115" y="62" transform="scale(0.8)"/>
        <text x="123" y="82" text-anchor="middle" fill="#333" font-size="6">ECS</text>
        
        <!-- RDS -->
        <use href="#rds-icon" x="145" y="62" transform="scale(0.8)"/>
        <text x="153" y="82" text-anchor="middle" fill="#333" font-size="6">RDS</text>
        
        <!-- Lambda -->
        <use href="#lambda-icon" x="115" y="90" transform="scale(0.8)"/>
        <text x="123" y="110" text-anchor="middle" fill="#333" font-size="6">Lambda</text>
        
        <!-- ElastiCache -->
        <use href="#redis-icon" x="145" y="90" transform="scale(0.8)"/>
        <text x="153" y="110" text-anchor="middle" fill="#333" font-size="6">ElastiCache</text>
        
        <!-- S3 (outside VPC) -->
        <use href="#s3-icon" x="25" y="105" transform="scale(0.8)"/>
        <text x="33" y="125" text-anchor="middle" fill="#333" font-size="6">S3</text>
        
        <!-- Connections -->
        <line x1="51" y1="70" x2="115" y2="70" stroke="#FF9900" stroke-width="1.5" marker-end="url(#arrowhead)"/>
        <line x1="131" y1="70" x2="145" y2="70" stroke="#FF9900" stroke-width="1.5" marker-end="url(#arrowhead)"/>
        <line x1="123" y1="82" x2="123" y2="90" stroke="#FF9900" stroke-width="1.5" marker-end="url(#arrowhead)"/>
        <line x1="131" y1="98" x2="145" y2="98" stroke="#FF9900" stroke-width="1.5" marker-end="url(#arrowhead)"/>
        
        <!-- Internet Gateway -->
        <rect x="85" y="28" width="30" height="12" fill="#FF9900" rx="2"/>
        <text x="100" y="36" text-anchor="middle" fill="white" font-size="6">IGW</text>
        <line x1="100" y1="25" x2="100" y2="28" stroke="#FF9900" stroke-width="2"/>
        <text x="100" y="22" text-anchor="middle" fill="#FF9900" font-size="6">Internet</text>
      </svg>
    `),
    type: "Cloud Architecture"
  }
];

const cloudProviders = [
  { 
    name: "AWS", 
    logo: "data:image/svg+xml;base64," + btoa(`
      <svg width="60" height="36" xmlns="http://www.w3.org/2000/svg">
        <rect width="60" height="36" fill="white" stroke="#e0e0e0"/>
        <path d="M15,12 Q15,10 18,10 Q22,9 26,10 Q30,10 30,12 Q28,14 26,14 L18,14 Q15,14 15,12" fill="#FF9900" opacity="0.8"/>
        <circle cx="18" cy="11.5" r="0.8" fill="white"/>
        <circle cx="22" cy="11" r="0.8" fill="white"/>
        <circle cx="26" cy="11.5" r="0.8" fill="white"/>
        <text x="30" y="24" text-anchor="middle" fill="#333" font-size="8" font-weight="600">AWS</text>
        <text x="30" y="32" text-anchor="middle" fill="#666" font-size="6">Amazon Web Services</text>
      </svg>
    `), 
    primary: true 
  },
  { 
    name: "Microsoft Azure", 
    logo: "data:image/svg+xml;base64," + btoa(`
      <svg width="60" height="36" xmlns="http://www.w3.org/2000/svg">
        <rect width="60" height="36" fill="white" stroke="#e0e0e0"/>
        <path d="M20,10 L25,10 L30,16 L35,16 L25,20 L20,16 Z" fill="#0078D4" opacity="0.8"/>
        <text x="30" y="26" text-anchor="middle" fill="#333" font-size="8" font-weight="600">Azure</text>
        <text x="30" y="32" text-anchor="middle" fill="#666" font-size="6">Microsoft Cloud</text>
      </svg>
    `) 
  },
  { 
    name: "Google Cloud", 
    logo: "data:image/svg+xml;base64," + btoa(`
      <svg width="60" height="36" xmlns="http://www.w3.org/2000/svg">
        <rect width="60" height="36" fill="white" stroke="#e0e0e0"/>
        <rect x="22" y="10" width="16" height="8" fill="#4285F4" opacity="0.8" rx="2"/>
        <circle cx="26" cy="14" r="1.5" fill="white"/>
        <circle cx="34" cy="14" r="1.5" fill="white"/>
        <text x="30" y="24" text-anchor="middle" fill="#333" font-size="8" font-weight="600">GCP</text>
        <text x="30" y="32" text-anchor="middle" fill="#666" font-size="6">Google Cloud</text>
      </svg>
    `) 
  },
  { 
    name: "Oracle Cloud", 
    logo: "data:image/svg+xml;base64," + btoa(`
      <svg width="60" height="36" xmlns="http://www.w3.org/2000/svg">
        <rect width="60" height="36" fill="white" stroke="#e0e0e0"/>
        <rect x="20" y="10" width="20" height="8" fill="#C74634" opacity="0.8" rx="4"/>
        <circle cx="25" cy="14" r="1" fill="white"/>
        <circle cx="30" cy="14" r="1" fill="white"/>
        <circle cx="35" cy="14" r="1" fill="white"/>
        <text x="30" y="24" text-anchor="middle" fill="#333" font-size="8" font-weight="600">OCI</text>
        <text x="30" y="32" text-anchor="middle" fill="#666" font-size="6">Oracle Cloud</text>
      </svg>
    `) 
  }
];

const targetAudiences = [
  {
    title: "For Enterprise Architects",
    subtitle: "Enterprise-Grade Architecture Documentation",
    description: "Generate complete TOGAF views (OV-1, OV-2, SV-1), ArchiMate models, and enterprise documents through intelligent questioning that captures business context, compliance requirements, and stakeholder needs.",
    features: [
      "TOGAF & ArchiMate Compliance",
      "Intelligent Requirements Gathering", 
      "Enterprise Tool Compatibility",
      "Multi-stakeholder Collaboration"
    ],
    icon: "🏢"
  },
  {
    title: "For Development Teams", 
    subtitle: "Technical Architecture & Implementation",
    description: "From high-level requirements to detailed technical specifications: sequence diagrams, class diagrams, deployment architectures, API specs, and cloud-optimized infrastructure code.",
    features: [
      "Progressive Technical Questioning",
      "Multi-Cloud Infrastructure Generation", 
      "Standards-Based Documentation",
      "Deploy-Ready Infrastructure Code"
    ],
    icon: "👨‍💻"
  }
];

const supportedOutputs = [
  {
    category: "Architecture Frameworks",
    items: ["TOGAF Enterprise Architecture views", "ArchiMate 3.2 compatible models", "BPMN 2.0 process diagrams", "UML 2.5 technical diagrams"]
  },
  {
    category: "Enterprise Documents", 
    items: ["Solution Architecture Document (SAD)", "High-Level Design Document (HLD)", "Software Design Document (SDD)", "API Specifications (OpenAPI/Swagger)"]
  },
  {
    category: "Export Formats",
    items: ["ArchiMate XML (tool compatibility)", "Visio XML (Microsoft integration)", "SVG/PNG/PDF (universal formats)", "Draw.io XML (Lucidchart import)"]
  },
  {
    category: "Cloud Platforms",
    items: ["Multi-cloud infrastructure generation", "Cost-optimized service selection", "Best-of-breed cloud services", "Vendor-agnostic deployment options"]
  }
];

const HomePage = ({ user }) => {
  // const navigate = useNavigate();
  // const { user: authUser } = useAuthenticator((context) => [context.user]);
  
  return (
    <Layout isPublic={true}>
      <div className="container homepage">
        <div className="page-heading">
          <h1>From Idea to Architecture in Minutes</h1>
          <div className="intro">
            <div className="intro-column">
              <p>Tell us what you want to build in plain language. DiagramLoom's AI conducts intelligent interviews, asking about business context, technical constraints, and scalability needs—then generates complete architecture packages with TOGAF artifacts, ArchiMate models, and deploy-ready infrastructure.</p>
            </div>
            <div className="intro-column">
              <p>Built for enterprise architects and development teams who need comprehensive documentation—not just diagrams. From initial concept to detailed technical specifications, with enterprise tool compatibility and multi-cloud optimization.</p>
            </div>
          </div>
          <div className="security-badge">
            <span className="security-icon">🔒</span>
            <span>Data never leaves your environment when running locally</span>
          </div>
          <div className="hero-cta">
            <a href={window.location.hostname === 'localhost' ? `http://localhost:${window.location.port || '3000'}/?app=true` : 'https://app.diagramloom.ai/'} className="primary-cta">Start Building Architecture</a>
            <a href="#how-it-works" className="secondary-cta">See How It Works</a>
          </div>
        </div>

        <section id="how-it-works" className="how-it-works">
          <h2 className="section-heading">How DiagramLoom Works</h2>
          <div className="steps-grid">
            {howItWorksSteps.map((step, index) => (
              <div className="step-item" key={index}>
                <div className="step-icon">{step.icon}</div>
                <div className="step-number">{step.step}</div>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="target-audiences">
          <div className="audiences-grid">
            {targetAudiences.map((audience, index) => (
              <div className="audience-item" key={index}>
                <div className="audience-icon">{audience.icon}</div>
                <h2>{audience.title}</h2>
                <h3>{audience.subtitle}</h3>
                <p>{audience.description}</p>
                <div className="audience-features">
                  <ul>
                    {audience.features.map((feature, idx) => (
                      <li key={idx}>
                        <span className="feature-check">✓</span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="sample-artifacts">
          <h2 className="section-heading">Sample Architecture Artifacts</h2>
          <p className="section-subtitle">See examples of what DiagramLoom generates from your requirements</p>
          <div className="artifacts-carousel">
            {sampleArtifacts.map((artifact, index) => (
              <div className="artifact-card" key={index}>
                <div className="artifact-thumbnail-container">
                  <img src={artifact.thumbnail} alt={artifact.title} className="artifact-thumbnail" />
                </div>
                <div className="artifact-info">
                  <span className="artifact-type">{artifact.type}</span>
                  <h4>{artifact.title}</h4>
                  <p>{artifact.description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="supported-outputs">
          <h2 className="section-heading">What You Get</h2>
          <div className="outputs-grid">
            {supportedOutputs.map((output, index) => (
              <div className="output-category" key={index}>
                <h3>{output.category}</h3>
                {output.category === "Cloud Platforms" ? (
                  <div className="cloud-providers">
                    {cloudProviders.map((provider, idx) => (
                      <div className={`cloud-badge ${provider.primary ? 'primary-cloud' : ''}`} key={idx}>
                        <img src={provider.logo} alt={provider.name} className="cloud-logo" loading="lazy" />
                        <span>{provider.name}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <ul>
                    {output.items.map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </section>

        <section className="privacy-flexibility">
          <h2 className="section-heading">Your Data, Your Choice</h2>
          <div className="privacy-grid">
            <div className="privacy-item">
              <h3>🔒 Local LLM Support</h3>
              <p>Run entirely on your infrastructure with local language models. Your sensitive architecture data never leaves your environment when running locally.</p>
            </div>
            <div className="privacy-item">
              <h3>☁️ Hybrid Cloud Options</h3>
              <p>Combine the best services across cloud providers for optimal cost and performance.</p>
            </div>
            <div className="privacy-item">
              <h3>🛡️ Enterprise Security</h3>
              <p>SOC 2 compliant with enterprise-grade security controls and audit trails.</p>
            </div>
          </div>
          <div className="final-cta">
            <h3>Transform Ideas into Architecture</h3>
            <p>Ready to revolutionize your architecture workflow? Start building comprehensive technical documentation with AI-powered requirements gathering and iterative refinement.</p>
            <a href={window.location.hostname === 'localhost' ? `http://localhost:${window.location.port || '3000'}/?app=true` : 'https://app.diagramloom.ai/'} className="final-cta-button">Get Started Now</a>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default HomePage;